import { useState, useEffect } from 'react';
import { Users, Plus, Trash2, UserPlus, X, Search, ChevronDown, ChevronUp, Crown, User } from 'lucide-react';
import apiClient from '../../api/client';

/**
 * Teams management tab for Settings page
 * Allows creating teams, adding/removing members, and managing team roles
 */
const TeamsTab = () => {
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [expandedTeams, setExpandedTeams] = useState({});
  
  // Create team form
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamDescription, setNewTeamDescription] = useState('');
  
  // Add member form
  const [showAddMember, setShowAddMember] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState('member');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load teams
      const teamsRes = await apiClient.get('/api/v1/goals/teams');
      setTeams(teamsRes.data.data || []);
      
      // Load available users
      const usersRes = await apiClient.get('/api/v1/goals/users/available');
      setUsers(usersRes.data.data || []);
      
      // Load team members for each team
      const teamsWithMembers = await Promise.all(
        (teamsRes.data.data || []).map(async (team) => {
          try {
            const membersRes = await apiClient.get(`/api/v1/goals/teams/${team.id}/members`);
            return { ...team, members: membersRes.data.data || [] };
          } catch (e) {
            return { ...team, members: [] };
          }
        })
      );
      setTeams(teamsWithMembers);
      
    } catch (error) {
      console.error('Error loading teams data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) {
      alert('Please enter a team name');
      return;
    }
    
    try {
      await apiClient.post('/api/v1/goals/teams', null, {
        params: {
          name: newTeamName,
          description: newTeamDescription || null
        }
      });
      
      setNewTeamName('');
      setNewTeamDescription('');
      setShowCreateTeam(false);
      loadData();
    } catch (error) {
      console.error('Error creating team:', error);
      alert('Failed to create team: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleAddMember = async (teamId) => {
    if (!selectedUserId) {
      alert('Please select a user');
      return;
    }
    
    try {
      await apiClient.post(`/api/v1/goals/teams/${teamId}/members`, null, {
        params: {
          user_id: selectedUserId,
          role: selectedRole
        }
      });
      
      setSelectedUserId('');
      setSelectedRole('member');
      setShowAddMember(null);
      loadData();
    } catch (error) {
      console.error('Error adding member:', error);
      alert('Failed to add member: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleRemoveMember = async (teamId, userId) => {
    if (!window.confirm('Are you sure you want to remove this member from the team?')) return;
    
    try {
      await apiClient.delete(`/api/v1/goals/teams/${teamId}/members/${userId}`);
      loadData();
    } catch (error) {
      console.error('Error removing member:', error);
      alert('Failed to remove member: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteTeam = async (teamId, teamName) => {
    if (!window.confirm(`Are you sure you want to delete "${teamName}"? This will remove all team memberships.`)) return;
    
    try {
      await apiClient.delete(`/api/v1/goals/teams/${teamId}`);
      loadData();
    } catch (error) {
      console.error('Error deleting team:', error);
      alert('Failed to delete team: ' + (error.response?.data?.detail || error.message));
    }
  };

  const toggleTeamExpanded = (teamId) => {
    setExpandedTeams(prev => ({
      ...prev,
      [teamId]: !prev[teamId]
    }));
  };

  const getRoleBadge = (role) => {
    const configs = {
      leader: { bg: 'bg-purple-100', text: 'text-purple-700', icon: Crown },
      manager: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Crown },
      admin: { bg: 'bg-red-100', text: 'text-red-700', icon: Crown },
      member: { bg: 'bg-gray-100', text: 'text-gray-700', icon: User }
    };
    const config = configs[role] || configs.member;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="w-3 h-3" />
        {role}
      </span>
    );
  };

  // Get users not in this team
  const getAvailableUsersForTeam = (team) => {
    const memberIds = (team.members || []).map(m => m.user_id);
    return users.filter(u => !memberIds.includes(u.id));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Loading teams...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Teams</h2>
          <p className="text-sm text-gray-600">Create and manage teams for goal tracking</p>
        </div>
        <button
          onClick={() => setShowCreateTeam(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          <Plus className="w-4 h-4" />
          Create Team
        </button>
      </div>

      {/* Create Team Form */}
      {showCreateTeam && (
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <h3 className="font-medium text-gray-900 mb-4">Create New Team</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Team Name *</label>
              <input
                type="text"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Sales Team Alpha"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={newTeamDescription}
                onChange={(e) => setNewTeamDescription(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Primary sales team for North America"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={() => {
                setShowCreateTeam(false);
                setNewTeamName('');
                setNewTeamDescription('');
              }}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateTeam}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create Team
            </button>
          </div>
        </div>
      )}

      {/* Teams List */}
      {teams.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500">No teams created yet</p>
          <p className="text-sm text-gray-400">Create a team to start organizing your sales force</p>
        </div>
      ) : (
        <div className="space-y-4">
          {teams.map((team) => (
            <div key={team.id} className="bg-white rounded-lg border shadow-sm">
              {/* Team Header */}
              <div
                className="p-4 flex justify-between items-center cursor-pointer hover:bg-gray-50"
                onClick={() => toggleTeamExpanded(team.id)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                    <Users className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{team.name}</h3>
                    <p className="text-sm text-gray-500">
                      {team.member_count || (team.members || []).length} members
                      {team.description && ` • ${team.description}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAddMember(showAddMember === team.id ? null : team.id);
                    }}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                    title="Add member"
                  >
                    <UserPlus className="w-5 h-5" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteTeam(team.id, team.name);
                    }}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    title="Delete team"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                  {expandedTeams[team.id] ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>

              {/* Add Member Form */}
              {showAddMember === team.id && (
                <div className="px-4 pb-4">
                  <div className="bg-gray-50 rounded-lg p-4 border">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Add Team Member</h4>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">User</label>
                        <select
                          value={selectedUserId}
                          onChange={(e) => setSelectedUserId(e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        >
                          <option value="">Select user...</option>
                          {getAvailableUsersForTeam(team).map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.name} ({user.role})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Role</label>
                        <select
                          value={selectedRole}
                          onChange={(e) => setSelectedRole(e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        >
                          <option value="member">Member</option>
                          <option value="leader">Team Leader</option>
                          <option value="manager">Manager</option>
                        </select>
                      </div>
                      <div className="flex items-end">
                        <button
                          onClick={() => handleAddMember(team.id)}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                        >
                          Add Member
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Team Members */}
              {expandedTeams[team.id] && (
                <div className="px-4 pb-4">
                  {(team.members || []).length === 0 ? (
                    <div className="text-center py-4 text-gray-500 text-sm">
                      No members in this team yet
                    </div>
                  ) : (
                    <div className="divide-y">
                      {(team.members || []).map((member) => (
                        <div key={member.user_id} className="py-3 flex justify-between items-center">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                              <User className="w-4 h-4 text-gray-500" />
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{member.user_name || member.email}</div>
                              <div className="text-xs text-gray-500">{member.email}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {getRoleBadge(member.role)}
                            <button
                              onClick={() => handleRemoveMember(team.id, member.user_id)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                              title="Remove from team"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Help Text */}
      <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
        <h4 className="font-medium text-gray-900 mb-2">About Teams</h4>
        <ul className="list-disc list-inside space-y-1">
          <li><strong>Teams</strong> group users together for goal tracking and performance management</li>
          <li><strong>Team Leaders</strong> can view the team dashboard and track all member progress</li>
          <li><strong>Members</strong> are assigned to team goals and appear on team leaderboards</li>
          <li>A user can belong to multiple teams</li>
        </ul>
      </div>
    </div>
  );
};

export default TeamsTab;
